<template>
    <div class="guide-rule">
        <resource-select
            :value="guideId"
            :options="guides"
            :loading="loadingResources"
            field="guideId"
            :read-only="readOnly"
            :check-valid-rule="checkValidRule"
            :placeholder="$t('guideRule.Select Guide')"
            @input="$emit('input', $event)">
            <template #noData>
                <div class="no-data-message">
                    {{ noDataText }}
                </div>
            </template>
        </resource-select>
        <template v-if="guideId">
            <template v-if="!isLocaleEnglish">
                <div class="flex-break" />
                <strong>{{ $t('Condition') }}</strong>
                <div class="flex-break" />
            </template>
            <fact-select
                :options="factOptions"
                :value="factValue"
                :read-only="readOnly"
                :check-valid-rule="checkValidRule"
                @input="$emit('input', $event)" />
            <template v-if="!isLocaleEnglish">
                <div class="flex-break" />
                <strong>{{ $t('Criteria') }}</strong>
                <div class="flex-break" />
            </template>
            <simple-select
                :options="timeOptions"
                :value="timeValue"
                :disabled="readOnly"
                :check-valid-rule="checkValidRule"
                preselect-first
                field="time"
                @input="$emit('input', $event)" />
            <component
                :is="valueComponent.component"
                v-if="valueComponent"
                :read-only="readOnly"
                :check-valid-rule="checkValidRule"
                v-bind="valueComponent.props"
                @input="$emit('input', $event)" />
        </template>
    </div>
</template>

<script>
import get from 'lodash/get';
import ResourceSelect from '@/segments/components/segment-editor-vue/components/resource-select.vue';
import NumberInput from '@/segments/components/segment-editor-vue/components/number-input.vue';
import FactSelect from '@/segments/components/segment-editor-vue/components/fact-select.vue';
import SimpleSelect from '@/segments/components/segment-editor-vue/components/simple-select.vue';
import AbsoluteDateRange from '@/segments/components/segment-editor-vue/components/absolute-date-range.vue';
import RelativeDateRange from '@/segments/components/segment-editor-vue/components/relative-date-range.vue';
import SingleDate from '@/segments/components/segment-editor-vue/components/single-date.vue';
import {
    getTimeOptions,
    getFactValue,
    getFactOptions,
    formatFactOptions,
    getNoResourcesFoundText,
    getNumberConfig,
    getRelativeDateRangeConfig,
    getAbsoluteDateRangeConfig,
    getSingleDateConfig,
} from '@/segments/components/segment-editor-vue/segment-editor.utils';

import { sanitizeState } from '@pendo/services/Formatters';

export default {
    name: 'GuideRule',
    components: {
        FactSelect,
        ResourceSelect,
        SimpleSelect,
        SingleDate,
        NumberInput,
        AbsoluteDateRange,
        RelativeDateRange,
    },
    props: {
        rule: {
            type: Object,
            required: true,
        },
        config: {
            type: Object,
            required: true,
        },
        resources: {
            type: Array,
            required: true,
        },
        loadingResources: {
            type: Boolean,
            default: false,
        },
        checkValidRule: {
            type: Boolean,
            default: false,
        },
        isMultiApp: {
            type: Boolean,
            default: false,
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
        isLocaleEnglish: {
            type: Boolean,
            default: true,
        },
    },
    emits: ['input'],
    computed: {
        noDataText () {
            return getNoResourcesFoundText({ rule: this.rule, isMultiApp: this.isMultiApp });
        },
        guides () {
            return this.resources
                .filter((guide) => {
                    const isResourceCenter = get(guide, 'attributes.resourceCenter') && guide.state !== 'public';
                    const isEmail = get(guide, 'attributes.isEmail');

                    return !isResourceCenter && !isEmail;
                })
                .map((guide) => {
                    return {
                        guideId: guide.id,
                        name: guide.name,
                        state: sanitizeState(guide).state,
                    };
                });
        },
        guideId () {
            return this.guides.find((guide) => guide.guideId === this.rule.guideId);
        },
        factOptions () {
            const options = getFactOptions(this.rule, this.config.onlyAllowGuideCompatibleRules);

            return formatFactOptions(options);
        },
        factValue () {
            return getFactValue(this.rule, this.factOptions);
        },
        timeValue () {
            return this.timeOptions.find((option) => option.time === this.rule.time);
        },
        timeOptions () {
            const options = [...getTimeOptions().guide];

            if (this.rule.fact === 'viewed') {
                const atLeastMostOptions = [
                    { time: 'atleast', name: this.$t('guideRule.at least') },
                    { time: 'atmost', name: this.$t('guideRule.at most') },
                ];

                return [...options, ...atLeastMostOptions];
            }

            if (this.rule.fact === 'lastviewed') {
                const betweenOption = { time: 'between', name: this.$t('guideRule.between') };

                return options.filter((option) => option.time !== 'ever').concat(betweenOption);
            }

            return options;
        },
        valueComponent () {
            switch (this.rule.time) {
                case 'withinLast':
                case '!withinLast':
                    return getRelativeDateRangeConfig(this.rule);
                case 'atmost':
                case 'atleast': {
                    const component = getNumberConfig(this.rule, 'value');
                    component.props.labels = { suffix: this.$t('guideRule.times') };

                    return component;
                }
                case '>=':
                case '<=':
                    return getSingleDateConfig(this.rule, 'first');
                case 'between':
                    return getAbsoluteDateRangeConfig(this.rule);
                default:
                    return false;
            }
        },
    },
};
</script>

<style scoped lang="less">
.flex-break {
    /* Reliable flex line break - forces next items to new line */
    flex-basis: 100%;
    height: 0;
    width: 0;
    overflow: hidden;
}
</style>
